import Phaser from "phaser";
import { FINAL_TOWER, LEVEL_CONFIG, MONSTERS, OBJECT_TYPES, SIZE_STAGES } from "../data/gameData.js";

const POWERUP_DEFS = [
  { id: 'speed', color: 0xffff44, accentColor: 0xffaa00, label: '⚡ Ускорение!', duration: 5 },
  { id: 'grow',  color: 0x44ff88, accentColor: 0x00cc44, label: '↑ Мегарост!' },
  { id: 'magnet',color: 0xff44ff, accentColor: 0xaa00ff, label: '◎ Притяжение!', duration: 4 },
];

const WORLD_BACKGROUND = 0x0a2131;
const SEA_COLOR = 0x05131d;
const ROAD_COLOR = 0x183542;
const PARK_COLOR = 0x123f46;
const PLAZA_COLOR = 0x1d4858;
const SHADOW_COLOR = 0x04080b;
const OBJECT_TYPE_MAP = new Map(OBJECT_TYPES.map((type) => [type.id, type]));

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function formatTime(seconds) {
  const safe = Math.max(0, Math.ceil(seconds));
  const minutes = String(Math.floor(safe / 60)).padStart(2, "0");
  const remainder = String(safe % 60).padStart(2, "0");
  return `${minutes}:${remainder}`;
}

function sampleWeighted(rng, entries) {
  const total = entries.reduce((sum, entry) => sum + entry.weight, 0);
  let cursor = rng.realInRange(0, total);

  for (const entry of entries) {
    cursor -= entry.weight;
    if (cursor <= 0) {
      return entry.id;
    }
  }

  return entries[entries.length - 1].id;
}

function getCurrentStage(size) {
  let result = SIZE_STAGES[0];

  for (const stage of SIZE_STAGES) {
    if (size >= stage.threshold) {
      result = stage;
    }
  }

  return result;
}

function getNextLockedType(swallowPower) {
  return OBJECT_TYPES.find((type) => type.requiredSize > swallowPower) ?? FINAL_TOWER;
}

function getUnlockedLabels(swallowPower) {
  return OBJECT_TYPES.filter((type) => type.requiredSize <= swallowPower)
    .slice(-3)
    .map((type) => type.label.toLowerCase());
}

export class CityScene extends Phaser.Scene {
  constructor(options) {
    super("city-scene");
    this.options = options;
  }

  preload() {
    // ── Show loading screen with progress bar ──────────────────────────────
    const loadingEl = document.getElementById('loading-screen');
    const progressBar = document.getElementById('loading-progress-bar');
    const progressText = document.getElementById('loading-progress-text');

    if (loadingEl) loadingEl.classList.remove('hidden');

    this.load.on('progress', (value) => {
      const pct = Math.round(value * 100);
      if (progressBar) progressBar.style.width = `${pct}%`;
      if (progressText) progressText.textContent = `${pct}%`;
    });

    this.load.on('complete', () => {
      if (loadingEl) {
        loadingEl.style.transition = 'opacity 0.3s ease';
        loadingEl.style.opacity = '0';
        setTimeout(() => loadingEl.classList.add('hidden'), 300);
      }
    });

    // Load per-monster sprite
    const monsterDef = MONSTERS.find((m) => m.id === this.options.monsterId);
    const sprite = monsterDef?.sprite ?? 'monster';
    this.load.image('monster-sprite', `/assets/${sprite}.png`);

    // Load individual powerup sprites
    this.load.image('powerup-speed', '/assets/powerup-speed.png');
    this.load.image('powerup-grow', '/assets/powerup-grow.png');
    this.load.image('powerup-magnet', '/assets/powerup-magnet.png');

    // Load building sprites
    this.load.image('obj-car',     '/assets/obj-car.png');
    this.load.image('obj-house',   '/assets/obj-house.png');
    this.load.image('obj-midrise', '/assets/obj-midrise.png');
    this.load.image('obj-tower',   '/assets/obj-tower.png');
    this.load.image('obj-crown',   '/assets/obj-crown.png');
    this.load.image('obj-kiosk',   '/assets/obj-kiosk.png');
    this.load.image('obj-garage',  '/assets/obj-garage.png');
    this.load.image('obj-block',   '/assets/obj-block.png');
    this.load.image('obj-tree',    '/assets/obj-tree.png');

    // Load guardian sprite
    this.load.image('guardian', '/assets/guardian.png');
  }

  create() {
    this.cameras.main.setBackgroundColor(WORLD_BACKGROUND);

    // Remove black/near-black background from sprite textures (handles JPEG artifacts)
    const SPRITE_KEYS = [
      'monster-sprite', 'guardian',
      'obj-car', 'obj-house', 'obj-midrise', 'obj-tower', 'obj-crown',
      'obj-kiosk', 'obj-garage', 'obj-block', 'obj-tree',
      'powerup-speed', 'powerup-grow', 'powerup-magnet',
    ];
    for (const key of SPRITE_KEYS) {
      this.applyBlackKeyTransparency(key, 32);
    }

    this.random = new Phaser.Math.RandomDataGenerator([`monster-city-${this.options.monsterId}`]);
    this.monsterConfig = MONSTERS.find((monster) => monster.id === this.options.monsterId) ?? MONSTERS[0];
    this.levelConfig = this.options.levelConfig ?? LEVEL_CONFIG;
    this.world = this.createWorldMetrics();
    this.entities = [];
    this.visualPool = [];
    this.deniedMessageCooldown = 0;
    this.lastHudPush = 0;
    this.statusUntil = 0;
    this.pointerTarget = new Phaser.Math.Vector2(this.world.startX, this.world.startY);
    this.pointerActive = false;
    this.joystickState = this.options.joystickState ?? null;
    this.isMobile = this.options.isMobile ?? false;
    this.keys = this.input.keyboard.addKeys({
      up: "W",
      left: "A",
      down: "S",
      right: "D",
      ability: "E",
      dash: Phaser.Input.Keyboard.KeyCodes.SPACE,
    });
    this.cursors = this.input.keyboard.createCursorKeys();

    this.state = {
      startedAt: this.time.now,
      elapsedSeconds: 0,
      timeLeft: this.levelConfig.timerSeconds,
      totalMass: 0,
      size: 0.9,
      consumedCount: 0,
      ended: false,
      dashCooldown: 0,
      dashTimer: 0,
      abilityCooldown: 0,
      abilityTimer: 0,
      stageName: getCurrentStage(0.9).name,
      comboCount: 0,
      lastConsumeTime: -Infinity,
      powerups: [],
      activePowerups: { speed: 0, magnet: 0 },
      guardians: [],
      guardiansSpawned: false,
      patrolGuardians: [],
      guardianHitCooldown: 0,
    };

    this.drawBackground();
    this.drawCityGrid();
    this.spawnDistricts();
    this.spawnPowerups();
    this.spawnFinalTower();
    this.spawnPatrolGuardians();
    this.createMonster();

    this.cameras.main.setBounds(0, 0, this.levelConfig.worldWidth, this.levelConfig.worldHeight);
    this.cameras.main.startFollow(this.monster.container, true, 0.11, 0.11, 0, 0);
    this.cameras.main.setZoom(1.08);

    this.input.on("pointerdown", (pointer) => {
      const worldPoint = pointer.positionToCamera(this.cameras.main);
      this.pointerTarget.set(worldPoint.x, worldPoint.y);
      this.pointerActive = true;
      void this.options.audio.unlock();
    });

    this.input.on("pointermove", (pointer) => {
      if (!pointer.isDown) {
        return;
      }

      const worldPoint = pointer.positionToCamera(this.cameras.main);
      this.pointerTarget.set(worldPoint.x, worldPoint.y);
      this.pointerActive = true;
    });

    this.input.on("pointerup", () => {
      this.pointerActive = false;
    });

    this.targetArrow = this.add.graphics();
    this.targetArrow.setDepth(200);
    this.targetArrow.setScrollFactor(0);
    this.nextTargetCheckTimer = 0;

    this.showStatus(`${this.monsterConfig.name} вырывается на улицы.`, 2500);
    this.pushHud(true);
  }

  update(_, deltaMs) {
    const delta = deltaMs / 1000;

    if (this.state.ended) {
      return;
    }

    this.state.elapsedSeconds += delta;
    this.state.timeLeft = Math.max(0, this.levelConfig.timerSeconds - this.state.elapsedSeconds);
    this.state.dashCooldown = Math.max(0, this.state.dashCooldown - delta);
    this.state.abilityCooldown = Math.max(0, this.state.abilityCooldown - delta);
    this.state.dashTimer = Math.max(0, this.state.dashTimer - delta);
    this.state.abilityTimer = Math.max(0, this.state.abilityTimer - delta);
    this.deniedMessageCooldown = Math.max(0, this.deniedMessageCooldown - delta);
    this.state.activePowerups.speed  = Math.max(0, this.state.activePowerups.speed  - delta);
    this.state.activePowerups.magnet = Math.max(0, this.state.activePowerups.magnet - delta);
    this.state.guardianHitCooldown   = Math.max(0, this.state.guardianHitCooldown   - delta);

    if (this.state.timeLeft <= 0) {
      this.finishRun(false, "Время вышло: башня пережила нашествие.");
      return;
    }

    this.handleAbilityInput();
    this.handleDashInput();
    this.updateMonsterMovement(delta);
    this.updateVacuum(delta);
    this.updateMagnet(delta);
    this.checkPowerupCollisions();
    this.checkConsumptions();

    if (!this.state.guardiansSpawned && this.state.size >= 5.0 && !this.state.ended) {
      this.spawnGuardians();
      this.state.guardiansSpawned = true;
    }
    this.updateGuardians(delta);
    this.updatePatrolGuardians(delta);

    this.nextTargetCheckTimer -= delta;
    if (this.nextTargetCheckTimer <= 0) {
      this.nextTargetCheckTimer = 0.5;
      this.updateTargetArrow();
    }

    this.updateMonsterPresentation(delta);
    this.updateCamera();
    this.pushHud();
  }

  createWorldMetrics() {
    const { worldWidth, worldHeight, grid } = this.levelConfig;
    const cityWidth = grid.columns * grid.blockSize + (grid.columns + 1) * grid.roadSize;
    const cityHeight = grid.rows * grid.blockSize + (grid.rows + 1) * grid.roadSize;
    const insetX = (worldWidth - cityWidth) / 2;
    const insetY = (worldHeight - cityHeight) / 2;

    return {
      cityWidth,
      cityHeight,
      insetX,
      insetY,
      centerX: worldWidth / 2,
      centerY: worldHeight / 2,
      startX: insetX + 120,
      startY: insetY + cityHeight - 120,
      maxDistance: Math.hypot(cityWidth / 2, cityHeight / 2),
    };
  }

  drawBackground() {
    const graphics = this.add.graphics();

    graphics.fillStyle(SEA_COLOR, 1);
    graphics.fillRect(0, 0, this.levelConfig.worldWidth, this.levelConfig.worldHeight);

    graphics.fillStyle(0x0b2f39, 1);
    graphics.fillRoundedRect(this.world.insetX - 52, this.world.insetY - 52, this.world.cityWidth + 104, this.world.cityHeight + 104, 220);

    graphics.fillStyle(0x123746, 1);
    graphics.fillRoundedRect(this.world.insetX - 10, this.world.insetY - 10, this.world.cityWidth + 20, this.world.cityHeight + 20, 176);

    graphics.fillStyle(0x0f222c, 1);
    graphics.fillCircle(this.world.centerX, this.world.centerY, 248);

    graphics.lineStyle(4, 0x2b7086, 0.45);
    graphics.strokeCircle(this.world.centerX, this.world.centerY, 214);
  }

  drawCityGrid() {
    const graphics = this.add.graphics();
    const { columns, rows, blockSize, roadSize } = this.levelConfig.grid;

    graphics.fillStyle(ROAD_COLOR, 1);
    graphics.fillRoundedRect(this.world.insetX, this.world.insetY, this.world.cityWidth, this.world.cityHeight, 150);

    for (let row = 0; row < rows; row += 1) {
      for (let col = 0; col < columns; col += 1) {
        const x = this.world.insetX + roadSize + col * (blockSize + roadSize);
        const y = this.world.insetY + roadSize + row * (blockSize + roadSize);
        const centerX = x + blockSize / 2;
        const centerY = y + blockSize / 2;
        const distance = Phaser.Math.Distance.Between(centerX, centerY, this.world.centerX, this.world.centerY);
        const isPlaza = distance < 240;
        const isPark = !isPlaza && this.random.frac() < 0.08;

        graphics.fillStyle(isPlaza ? PLAZA_COLOR : isPark ? PARK_COLOR : 0x0f2835, 1);
        graphics.fillRoundedRect(x, y, blockSize, blockSize, isPlaza ? 28 : 18);

        if (isPark) {
          // Place tree sprites; they are static decorations (not entities)
          const treeDefs = [
            { ox: 34, oy: 48, size: 38 },
            { ox: 84, oy: 92, size: 50 },
            { ox: 120, oy: 48, size: 32 },
          ];
          for (const td of treeDefs) {
            const treeImg = this.add.image(x + td.ox, y + td.oy, 'obj-tree');
            treeImg.setDisplaySize(td.size, td.size);
            treeImg.setDepth(8);
          }
        }
      }
    }
  }

  spawnDistricts() {
    const { columns, rows, blockSize, roadSize } = this.levelConfig.grid;

    for (let row = 0; row < rows; row += 1) {
      for (let col = 0; col < columns; col += 1) {
        const x = this.world.insetX + roadSize + col * (blockSize + roadSize);
        const y = this.world.insetY + roadSize + row * (blockSize + roadSize);
        const centerX = x + blockSize / 2;
        const centerY = y + blockSize / 2;
        const distance = Phaser.Math.Distance.Between(centerX, centerY, this.world.centerX, this.world.centerY);

        if (distance < 270) {
          continue;
        }

        const zone = distance / this.world.maxDistance;
        const count = this.getLotPopulation(zone);
        const anchors = this.getLotAnchors(count, x, y, blockSize, blockSize);

        anchors.forEach((anchor) => {
          const type = this.pickTypeForZone(zone);
          this.spawnEntity(type, anchor.x, anchor.y, false);
        });
      }
    }
  }

  getLotPopulation(zone) {
    if (zone > 0.74) {
      return this.random.between(3, 4);
    }

    if (zone > 0.58) {
      return this.random.between(2, 3);
    }

    if (zone > 0.42) {
      return this.random.between(1, 2);
    }

    return 1;
  }

  getLotAnchors(count, x, y, width, height) {
    const presets = {
      1: [[0.5, 0.5]],
      2: [[0.32, 0.5], [0.68, 0.5]],
      3: [[0.32, 0.34], [0.7, 0.38], [0.5, 0.72]],
      4: [[0.28, 0.3], [0.72, 0.3], [0.3, 0.72], [0.7, 0.7]],
    };

    return presets[count].map(([sx, sy]) => ({
      x: x + width * sx + this.random.between(-12, 12),
      y: y + height * sy + this.random.between(-12, 12),
    }));
  }

  pickTypeForZone(zone) {
    const outer = [
      { id: "pedestrian", weight: 5 },
      { id: "bin", weight: 5 },
      { id: "fence", weight: 4 },
      { id: "lamp", weight: 4 },
      { id: "car", weight: 2 },
    ];
    const midOuter = [
      { id: "pedestrian", weight: 2 },
      { id: "car", weight: 4 },
      { id: "kiosk", weight: 4 },
      { id: "garage", weight: 3 },
      { id: "house", weight: 2 },
    ];
    const mid = [
      { id: "garage", weight: 3 },
      { id: "house", weight: 4 },
      { id: "midrise", weight: 3 },
      { id: "block", weight: 1 },
    ];
    const inner = [
      { id: "house", weight: 2 },
      { id: "midrise", weight: 4 },
      { id: "block", weight: 3 },
      { id: "tower", weight: 2 },
    ];

    if (zone > 0.74) {
      return OBJECT_TYPE_MAP.get(sampleWeighted(this.random, outer)) ?? OBJECT_TYPES[0];
    }

    if (zone > 0.58) {
      return OBJECT_TYPE_MAP.get(sampleWeighted(this.random, midOuter)) ?? OBJECT_TYPES[0];
    }

    if (zone > 0.4) {
      return OBJECT_TYPE_MAP.get(sampleWeighted(this.random, mid)) ?? OBJECT_TYPES[0];
    }

    return OBJECT_TYPE_MAP.get(sampleWeighted(this.random, inner)) ?? OBJECT_TYPES[0];
  }

  spawnEntity(type, x, y, isFinal) {
    const visual = this.createEntityVisual(type, x, y, isFinal);

    const entity = {
      id: `${type.id}-${this.entities.length}`,
      type,
      x,
      y,
      radius: type.radius,
      display: visual,
      alive: true,
      isFinal,
      warnedAt: 0,
    };

    this.entities.push(entity);
    return entity;
  }

  spawnFinalTower() {
    const tower = this.spawnEntity(FINAL_TOWER, this.world.centerX, this.world.centerY, true);

    this.tweens.add({
      targets: tower.display,
      scaleX: 1.04,
      scaleY: 1.04,
      duration: 1800,
      yoyo: true,
      repeat: -1,
      ease: "Sine.easeInOut",
    });
  }

  createEntityVisual(type, x, y, isFinal) {
    const container = this.add.container(x, y);
    container.setDepth(isFinal ? 28 : 14 + Math.round(type.requiredSize));

    const shadow = this.add.ellipse(0, type.height * 0.28, type.width * 0.85, type.height * 0.42, SHADOW_COLOR, 0.28);
    const body = this.add.rectangle(0, 0, type.width, type.height, type.color, isFinal ? 0.95 : 0.92);
    body.setStrokeStyle(3, 0xffffff, 0.1);

    container.add([shadow]);

    switch (type.style) {
      case "person": {
        const head = this.add.circle(0, -6, 7, 0xfff4cf, 1);
        const torso = this.add.ellipse(0, 8, 12, 18, type.color, 1);
        container.add([head, torso]);
        break;
      }
      case "lamp": {
        const pole = this.add.rectangle(0, 3, 6, 28, 0xcdefff, 1);
        const bulb = this.add.circle(0, -12, 8, 0xfff2b3, 1);
        container.add([pole, bulb]);
        break;
      }
      case "fence": {
        container.add(body);
        for (let index = -2; index <= 2; index += 1) {
          const post = this.add.rectangle(index * 8, 0, 4, 14, 0xffd7a8, 1);
          container.add(post);
        }
        break;
      }
      case "bin": {
        body.setDisplaySize(type.width * 0.8, type.height * 0.86);
        container.add(body);
        container.add(this.add.rectangle(0, -10, type.width * 0.55, 4, 0xd8fff0, 0.85));
        break;
      }
      case "car": {
        // shadow already in container; sprite replaces procedural body
        const carSprite = this.add.image(0, 0, 'obj-car');
        carSprite.setDisplaySize(type.width + 10, type.height + 10);
        container.add(carSprite);
        break;
      }
      case "kiosk": {
        const kioskSprite = this.add.image(0, 0, 'obj-kiosk');
        kioskSprite.setDisplaySize(type.width + 10, type.height + 10);
        container.add(kioskSprite);
        break;
      }
      case "garage": {
        const garageSprite = this.add.image(0, 0, 'obj-garage');
        garageSprite.setDisplaySize(type.width + 12, type.height + 12);
        container.add(garageSprite);
        break;
      }
      case "house": {
        const houseSprite = this.add.image(0, 0, 'obj-house');
        houseSprite.setDisplaySize(type.width + 14, type.height + 14);
        container.add(houseSprite);
        break;
      }
      case "midrise": {
        const midriseSprite = this.add.image(0, 0, 'obj-midrise');
        midriseSprite.setDisplaySize(type.width + 16, type.height + 16);
        container.add(midriseSprite);
        break;
      }
      case "block": {
        const blockSprite = this.add.image(0, 0, 'obj-block');
        blockSprite.setDisplaySize(type.width + 20, type.height + 20);
        container.add(blockSprite);
        break;
      }
      case "tower": {
        const towerSprite = this.add.image(0, 0, 'obj-tower');
        towerSprite.setDisplaySize(type.width + 22, type.height + 22);
        container.add(towerSprite);
        break;
      }
      case "crown": {
        const crownSprite = this.add.image(0, 0, 'obj-crown');
        crownSprite.setDisplaySize(type.width + 32, type.height + 32);
        this.tweens.add({
          targets: crownSprite,
          rotation: Math.PI * 2,
          duration: 8000,
          repeat: -1,
          ease: 'Linear',
        });
        container.add(crownSprite);
        break;
      }
      default:
        container.add(body);
    }

    return container;
  }

  createMonster() {
    const container = this.add.container(this.world.startX, this.world.startY);
    container.setDepth(50);

    // Glow aura
    const glow = this.add.ellipse(0, 0, 140, 140, this.monsterConfig.color, 0.22);
    this.tweens.add({
      targets: glow,
      scaleX: 1.22, scaleY: 1.22,
      alpha: 0.35,
      duration: 1100,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    // Shadow
    const shadow = this.add.ellipse(0, 30, 100, 40, SHADOW_COLOR, 0.32);

    // Pixel-art monster sprite — black key transparency applied in create()
    const sprite = this.add.image(0, 0, 'monster-sprite');
    sprite.setDisplaySize(108, 108);

    container.add([glow, shadow, sprite]);

    this.monster = {
      container,
      body: sprite,         // used for flash effect in guardian collision
      belly: null,
      eyes: [],
      pupils: [],
      sprite,
      velocity: new Phaser.Math.Vector2(),
      baseScale: 0.95,
      breathe: 0,
    };

    this.updateMonsterScale(true);
  }

  handleDashInput() {
    if (Phaser.Input.Keyboard.JustDown(this.keys.dash) && this.state.dashCooldown <= 0) {
      this.state.dashCooldown = 3.5;
      this.state.dashTimer = 0.28;
      this.options.audio.playDash();
      this.showStatus("Рывок!", 900);
    }
  }

  handleAbilityInput() {
    if (Phaser.Input.Keyboard.JustDown(this.keys.ability) && this.state.abilityCooldown <= 0) {
      this.state.abilityCooldown = this.monsterConfig.ability.cooldown;
      this.state.abilityTimer = this.monsterConfig.ability.duration;
      this.options.audio.playAbility();
      this.showStatus(`${this.monsterConfig.ability.name} активирован.`, 1700);
    }
  }

  updateMonsterMovement(delta) {
    const keyboardDirection = new Phaser.Math.Vector2(
      Number(this.cursors.right.isDown || this.keys.right.isDown) - Number(this.cursors.left.isDown || this.keys.left.isDown),
      Number(this.cursors.down.isDown || this.keys.down.isDown) - Number(this.cursors.up.isDown || this.keys.up.isDown),
    );

    const direction = keyboardDirection.lengthSq() > 0 ? keyboardDirection.normalize() : new Phaser.Math.Vector2();

    // Mobile joystick input takes priority
    if (this.isMobile && this.joystickState?.active) {
      direction.set(this.joystickState.x, this.joystickState.y);
    } else if (direction.lengthSq() === 0 && this.pointerActive) {
      direction.copy(this.pointerTarget).subtract(this.monster.container);
      if (direction.length() < 18) {
        direction.set(0, 0);
      } else {
        direction.normalize();
      }
    }

    const weightPenalty = clamp(this.state.size * 0.043, 0, 0.36);
    const dashBoost = this.state.dashTimer > 0 ? this.monsterConfig.dashMultiplier : 1;
    const speedBoost = this.state.activePowerups.speed > 0 ? 1.5 : 1;
    const baseSpeed = this.monsterConfig.speed * (1 - weightPenalty) * dashBoost * speedBoost;

    this.monster.velocity.lerp(direction.scale(baseSpeed), 0.14);
    this.monster.container.x += this.monster.velocity.x * delta;
    this.monster.container.y += this.monster.velocity.y * delta;

    this.monster.container.x = clamp(this.monster.container.x, this.world.insetX + 20, this.world.insetX + this.world.cityWidth - 20);
    this.monster.container.y = clamp(this.monster.container.y, this.world.insetY + 20, this.world.insetY + this.world.cityHeight - 20);
  }

  updateVacuum(delta) {
    if (this.monsterConfig.ability.type !== "vacuum" || this.state.abilityTimer <= 0) {
      return;
    }

    const pullRadius = this.monsterConfig.ability.radius;

    for (const entity of this.entities) {
      if (!entity.alive || entity.isFinal || entity.type.requiredSize > 1.8) {
        continue;
      }

      const distance = Phaser.Math.Distance.Between(entity.x, entity.y, this.monster.container.x, this.monster.container.y);

      if (distance > pullRadius || distance < 10) {
        continue;
      }

      const pull = new Phaser.Math.Vector2(this.monster.container.x - entity.x, this.monster.container.y - entity.y).normalize();
      const amount = clamp((pullRadius - distance) / pullRadius, 0.15, 1);
      entity.x += pull.x * 110 * amount * delta;
      entity.y += pull.y * 110 * amount * delta;
      entity.display.setPosition(entity.x, entity.y);
    }
  }

  checkConsumptions() {
    const swallowPower = this.getSwallowPower();
    const monsterRadius = this.getMonsterRadius();

    for (const entity of this.entities) {
      if (!entity.alive) {
        continue;
      }

      const distance = Phaser.Math.Distance.Between(entity.x, entity.y, this.monster.container.x, this.monster.container.y);
      const touchDistance = monsterRadius + entity.radius * 0.62;

      if (distance > touchDistance) {
        continue;
      }

      if (swallowPower >= entity.type.requiredSize) {
        this.consumeEntity(entity);
        continue;
      }

      if (this.deniedMessageCooldown <= 0) {
        this.deniedMessageCooldown = 0.75;
        this.options.audio.playDenied();
        this.showStatus(`Пока рано: ${entity.type.name.toLowerCase()} требуют размер ${entity.type.requiredSize.toFixed(1)}.`, 1200);
        this.tweens.add({
          targets: entity.display,
          x: entity.x + 10,
          duration: 60,
          yoyo: true,
          repeat: 2,
          ease: "Sine.easeInOut",
          onComplete: () => entity.display.setPosition(entity.x, entity.y),
        });
      }
    }
  }

  consumeEntity(entity) {
    entity.alive = false;
    this.state.consumedCount += 1;

    const stageBefore = getCurrentStage(this.state.size);

    const COMBO_WINDOW_MS = 2000;
    const now = this.time.now;
    if (now - this.state.lastConsumeTime < COMBO_WINDOW_MS) {
      this.state.comboCount = Math.min(this.state.comboCount + 1, 4);
    } else {
      this.state.comboCount = 0;
    }
    this.state.lastConsumeTime = now;
    const comboMultiplier = 1 + this.state.comboCount; // 1..5

    const gainMultiplier = this.monsterConfig.growthMultiplier * (this.monsterConfig.ability.type === "growth" && this.state.abilityTimer > 0 ? 1.65 : 1) * comboMultiplier;

    if (this.state.comboCount >= 2) {
      this.spawnComboText(comboMultiplier);
    }

    this.state.totalMass += entity.type.mass * gainMultiplier;
    this.state.size = 0.9 + Math.sqrt(this.state.totalMass) * 0.93;
    this.state.stageName = getCurrentStage(this.state.size).name;

    this.options.audio.playEat(entity.type.mass);
    this.spawnConsumeBurst(entity);

    this.tweens.killTweensOf(entity.display);
    this.tweens.add({
      targets: entity.display,
      alpha: 0,
      scaleX: 0.2,
      scaleY: 0.2,
      duration: 180,
      ease: "Quad.easeIn",
      onComplete: () => entity.display.destroy(),
    });

    this.updateMonsterScale(false);

    const stageAfter = getCurrentStage(this.state.size);

    if (stageAfter.name !== stageBefore.name) {
      this.options.audio.playGrowth();
      this.showStatus(`${stageAfter.name}: ${stageAfter.flavor}`, 1800);
    }

    if (entity.isFinal) {
      this.finishRun(true, `${this.monsterConfig.name} проглотил главную башню.`);
    }
  }

  spawnConsumeBurst(entity) {
    const extraColors = [0xffffff, 0xffff88, entity.type.color];
    for (let i = 0; i < 14; i++) {
      const color = extraColors[i % extraColors.length];
      const shard = this.add.circle(entity.x, entity.y, this.random.between(5, 12), color, 0.95);
      const angle = Phaser.Math.DegToRad(this.random.between(0, 360));
      const distance = this.random.between(30, 75);
      this.tweens.add({
        targets: shard,
        x: entity.x + Math.cos(angle) * distance,
        y: entity.y + Math.sin(angle) * distance,
        alpha: 0,
        scale: 0.2,
        duration: 320,
        ease: 'Quad.easeOut',
        onComplete: () => shard.destroy(),
      });
    }
  }

  updateMonsterScale(force) {
    const nextScale = 0.56 + this.state.size * 0.19;

    if (force) {
      this.monster.container.setScale(nextScale);
      return;
    }

    this.tweens.killTweensOf(this.monster.container);
    this.tweens.add({
      targets: this.monster.container,
      scaleX: nextScale,
      scaleY: nextScale,
      duration: 180,
      ease: "Back.easeOut",
    });
  }

  updateMonsterPresentation(delta) {
    this.monster.breathe += delta * 3.4;
    const wobble = Math.sin(this.monster.breathe) * 0.02;
    const stretch = this.state.dashTimer > 0 ? 0.08 : 0;
    const squash = this.state.abilityTimer > 0 ? 0.03 : 0;
    const baseScale = 0.56 + this.state.size * 0.19;

    this.monster.container.scaleX = baseScale + wobble + stretch;
    this.monster.container.scaleY = baseScale - wobble * 0.5 - squash;

    // Rotate sprite to face movement direction
    if (this.monster.sprite && this.monster.velocity.length() > 30) {
      const angle = Math.atan2(this.monster.velocity.y, this.monster.velocity.x);
      this.monster.sprite.setRotation(angle + Math.PI / 2);
    }
  }

  updateCamera() {
    const targetZoom = clamp(1.14 - this.state.size * 0.055, 0.38, 1.06);
    this.cameras.main.zoom = Phaser.Math.Linear(this.cameras.main.zoom, targetZoom, 0.05);
  }

  /**
   * Постобработка текстуры: пиксели темнее threshold (усреднённая яркость RGB)
   * становятся полностью прозрачными. Устраняет видимый квадратный фон у JPEG-спрайтов.
   */
  applyBlackKeyTransparency(textureKey, threshold = 32) {
    if (!this.textures.exists(textureKey)) return;
    try {
      const texture = this.textures.get(textureKey);
      const src = texture.getSourceImage();
      if (!src || src.width === 0) return;

      const canvas = document.createElement('canvas');
      canvas.width  = src.width;
      canvas.height = src.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(src, 0, 0);

      const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const d = imgData.data;
      for (let i = 0; i < d.length; i += 4) {
        const brightness = (d[i] + d[i + 1] + d[i + 2]) / 3;
        if (brightness < threshold) {
          d[i + 3] = 0; // fully transparent
        }
      }
      ctx.putImageData(imgData, 0, 0);

      this.textures.remove(textureKey);
      this.textures.addCanvas(textureKey, canvas);
    } catch (e) {
      // canvas tainted (cross-origin) or texture unavailable — skip silently
    }
  }

  getSwallowPower() {
    const wideBoost = this.monsterConfig.ability.type === "wide" && this.state.abilityTimer > 0 ? 1.15 : 0;
    return this.state.size * this.monsterConfig.widthBonus + wideBoost;
  }

  getMonsterRadius() {
    // Match visual sprite size: sprite is 108px, container scale = 0.56 + size*0.19
    // visual radius ≈ 54 * (0.56 + size * 0.19) = 30 + size * 10.3
    return 28 + this.state.size * 10;
  }

  showStatus(text, duration) {
    this.statusUntil = this.time.now + duration;
    this.options.onStatus(text);
  }

  pushHud(force = false) {
    if (!force && this.time.now - this.lastHudPush < 120) {
      return;
    }

    this.lastHudPush = this.time.now;

    const swallowPower = this.getSwallowPower();
    const currentStage = getCurrentStage(this.state.size);
    const nextType = getNextLockedType(swallowPower);
    const unlockedLabels = getUnlockedLabels(swallowPower);
    const towerProgress = clamp((swallowPower / FINAL_TOWER.requiredSize) * 100, 0, 100);
    const abilityActive = this.state.abilityTimer > 0;

    this.options.onHudChange({
      stage: currentStage.name,
      size: this.state.size.toFixed(2),
      consumed: String(this.state.consumedCount),
      time: formatTime(this.state.timeLeft),
      dash: this.state.dashCooldown <= 0 ? "Готов" : `${this.state.dashCooldown.toFixed(1)} c`,
      ability:
        abilityActive
          ? `${this.monsterConfig.ability.name}: ${this.state.abilityTimer.toFixed(1)} c`
          : this.state.abilityCooldown <= 0
            ? `${this.monsterConfig.ability.name}: готово`
            : `${this.monsterConfig.ability.name}: ${this.state.abilityCooldown.toFixed(1)} c`,
      tower: `${Math.round(towerProgress)}%`,
      towerPercent: towerProgress,
      targets:
        unlockedLabels.length > 0
          ? `Сейчас можно есть: ${unlockedLabels.join(", ")}. Следующая цель: ${nextType.label.toLowerCase()} от размера ${nextType.requiredSize.toFixed(1)}.`
          : `Сейчас доступны только самые мелкие объекты. Следующая цель: ${nextType.label.toLowerCase()}.`,
      objective:
        towerProgress >= 100
          ? "Главная башня уже по силам. Доберись до центра и проглоти ее."
          : `До главной башни нужно еще ${Math.max(0, FINAL_TOWER.requiredSize - swallowPower).toFixed(2)} размера.`,
    });
  }

  finishRun(won, summary) {
    if (this.state.ended) {
      return;
    }

    if (this.targetArrow) this.targetArrow.clear();
    this.state.ended = true;
    if (won) {
      this.options.audio.playWin();
    } else {
      this.options.audio.playLose();
    }

    this.options.onResult({
      won,
      summary,
      monsterId: this.monsterConfig.id,
      monsterName: this.monsterConfig.name,
      timeSpent: this.state.elapsedSeconds,
      timeLeft: this.state.timeLeft,
      consumed: this.state.consumedCount,
      size: this.state.size,
      stars: won
        ? (this.state.timeLeft >= 60 ? 3 : this.state.timeLeft >= 20 ? 2 : 1)
        : 0,
    });
  }

  // ── Combo ──────────────────────────────────────────────────────────────────

  spawnComboText(multiplier) {
    const colors = ['#ffdd00', '#ff9900', '#ff4444', '#ff00ff', '#00ffff'];
    const color = colors[multiplier - 2] ?? '#ffdd00';
    const text = this.add.text(
      this.monster.container.x,
      this.monster.container.y - 70,
      `\u00d7${multiplier} \u041a\u041e\u041c\u0411\u041e!`,
      { fontSize: '32px', fontFamily: 'monospace', color, stroke: '#000000', strokeThickness: 5 }
    );
    text.setDepth(150).setOrigin(0.5);
    this.tweens.add({
      targets: text,
      y: text.y - 90,
      alpha: 0,
      scaleX: 1.5,
      scaleY: 1.5,
      duration: 950,
      ease: 'Quad.easeOut',
      onComplete: () => text.destroy(),
    });
  }

  // ── Power-ups ──────────────────────────────────────────────────────────────

  spawnPowerups() {
    const count = this.random.between(5, 8);
    const { insetX, insetY, cityWidth, cityHeight } = this.world;
    const margin = 200;
    for (let i = 0; i < count; i++) {
      const x = this.random.between(insetX + margin, insetX + cityWidth - margin);
      const y = this.random.between(insetY + margin, insetY + cityHeight - margin);
      const def = POWERUP_DEFS[i % POWERUP_DEFS.length];
      this.spawnPowerup(def, x, y);
    }
  }

  spawnPowerup(def, x, y) {
    const container = this.add.container(x, y);
    container.setDepth(20);
    const glow = this.add.circle(0, 0, 28, def.color, 0.18);
    const body = this.add.circle(0, 0, 20, def.color, 1);
    body.setStrokeStyle(3, def.accentColor, 1);
    const icon = this.add.image(0, -2, `powerup-${def.id}`);
    icon.setDisplaySize(30, 30);
    container.add([glow, body, icon]);
    this.tweens.add({
      targets: container,
      scaleX: 1.15,
      scaleY: 1.15,
      duration: 700,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
    const powerup = { id: def.id, def, x, y, radius: 22, display: container, alive: true };
    this.state.powerups.push(powerup);
  }

  checkPowerupCollisions() {
    const monsterRadius = this.getMonsterRadius();
    for (const pu of this.state.powerups) {
      if (!pu.alive) continue;
      const dist = Phaser.Math.Distance.Between(pu.x, pu.y, this.monster.container.x, this.monster.container.y);
      if (dist > monsterRadius + pu.radius) continue;
      this.collectPowerup(pu);
    }
  }

  collectPowerup(pu) {
    pu.alive = false;
    this.tweens.killTweensOf(pu.display);
    this.tweens.add({
      targets: pu.display,
      scaleX: 2.5, scaleY: 2.5, alpha: 0,
      duration: 300, ease: 'Quad.easeOut',
      onComplete: () => pu.display.destroy(),
    });
    if (pu.def.id === 'speed') {
      this.state.activePowerups.speed = pu.def.duration;
    } else if (pu.def.id === 'magnet') {
      this.state.activePowerups.magnet = pu.def.duration;
    } else if (pu.def.id === 'grow') {
      this.state.totalMass += 1.5;
      this.state.size = 0.9 + Math.sqrt(this.state.totalMass) * 0.93;
      this.state.stageName = getCurrentStage(this.state.size).name;
      this.updateMonsterScale(false);
    }
    this.options.audio.playGrowth();
    this.showStatus(pu.def.label, 2000);
  }

  updateMagnet(delta) {
    if (this.state.activePowerups.magnet <= 0) return;
    const pullRadius = 300;
    for (const entity of this.entities) {
      if (!entity.alive || entity.isFinal || entity.type.requiredSize > this.getSwallowPower()) continue;
      const distance = Phaser.Math.Distance.Between(entity.x, entity.y, this.monster.container.x, this.monster.container.y);
      if (distance > pullRadius || distance < 10) continue;
      const pull = new Phaser.Math.Vector2(this.monster.container.x - entity.x, this.monster.container.y - entity.y).normalize();
      const amount = clamp((pullRadius - distance) / pullRadius, 0.15, 1);
      entity.x += pull.x * 130 * amount * delta;
      entity.y += pull.y * 130 * amount * delta;
      entity.display.setPosition(entity.x, entity.y);
    }
  }

  // ── Guardians ──────────────────────────────────────────────────────────────

  spawnGuardians() {
    const defs = [
      { angle: 0,       color: 0x4488ff, accentColor: 0x2255cc },
      { angle: Math.PI, color: 0xff4444, accentColor: 0xcc2222 },
    ];
    for (const def of defs) {
      const container = this.add.container(0, 0);
      container.setDepth(30);
      const sprite = this.add.image(0, 0, 'guardian');
      sprite.setDisplaySize(90, 90);
      container.add([sprite]);
      this.state.guardians.push({ angle: def.angle, speed: 0.7, radius: 300, display: container });
    }
    this.showStatus('\u0413\u0430\u0440\u0434\u0438\u0430\u043d\u044b \u043f\u043e\u044f\u0432\u0438\u043b\u0438\u0441\u044c! \u0411\u0443\u0434\u044c \u043e\u0441\u0442\u043e\u0440\u043e\u0436\u0435\u043d.', 2500);
  }

  updateGuardians(delta) {
    if (this.state.guardians.length === 0) return;
    const hitRadius = 44 + this.getMonsterRadius() * 0.35;
    for (const g of this.state.guardians) {
      g.angle += g.speed * delta;
      const gx = this.world.centerX + Math.cos(g.angle) * g.radius;
      const gy = this.world.centerY + Math.sin(g.angle) * g.radius;
      g.display.setPosition(gx, gy);
      g.display.setRotation(g.angle + Math.PI / 2);

      if (this.state.guardianHitCooldown > 0) continue;
      const dist = Phaser.Math.Distance.Between(gx, gy, this.monster.container.x, this.monster.container.y);
      if (dist < hitRadius) {
        this._applyGuardianHit(gx, gy, 200, '\u0421\u0442\u0440\u0430\u0436 \u0431\u0430\u0448\u043d\u0438 \u0430\u0442\u0430\u043a\u0443\u0435\u0442! -\u043c\u0430\u0441\u0441\u0430');
      }
    }
  }

  /** \u041e\u0431\u0449\u0438\u0439 \u044d\u0444\u0444\u0435\u043a\u0442 \u043f\u043e\u043f\u0430\u0434\u0430\u043d\u0438\u044f \u0441\u0442\u0440\u0430\u0436\u043d\u0438\u043a\u0430 */
  _applyGuardianHit(gx, gy, pushForce, message) {
    if (this.state.guardianHitCooldown > 0) return;
    this.state.guardianHitCooldown = 1.5;

    // \u0428\u0442\u0440\u0430\u0444 \u043a \u0440\u0430\u0437\u043c\u0435\u0440\u0443
    const penalty = Math.min(0.4, this.state.size * 0.06);
    this.state.totalMass = Math.max(0.1, this.state.totalMass - penalty * 5);
    this.state.size = Math.max(0.9, 0.9 + Math.sqrt(Math.max(0, this.state.totalMass)) * 0.93);
    this.state.stageName = getCurrentStage(this.state.size).name;
    this.updateMonsterScale(false);

    // \u0418\u043c\u043f\u0443\u043b\u044c\u0441 \u043e\u0442\u0442\u0430\u043b\u043a\u0438\u0432\u0430\u043d\u0438\u044f
    const pushDir = new Phaser.Math.Vector2(
      this.monster.container.x - gx,
      this.monster.container.y - gy
    ).normalize();
    this.monster.container.x += pushDir.x * pushForce;
    this.monster.container.y += pushDir.y * pushForce;
    this.monster.container.x = clamp(this.monster.container.x, this.world.insetX + 20, this.world.insetX + this.world.cityWidth - 20);
    this.monster.container.y = clamp(this.monster.container.y, this.world.insetY + 20, this.world.insetY + this.world.cityHeight - 20);

    // \u041c\u0438\u0433\u0430\u043d\u0438\u0435
    this.tweens.add({
      targets: this.monster.body,
      alpha: 0.1,
      duration: 60,
      yoyo: true,
      repeat: 4,
    });
    this.showStatus(message, 1200);
  }

  // ── Patrol Guardians ───────────────────────────────────────────────────────

  spawnPatrolGuardians() {
    const { insetX, insetY, cityWidth, cityHeight } = this.world;
    const count = 4;
    const margin = 250;
    for (let i = 0; i < count; i++) {
      const x = insetX + margin + this.random.frac() * (cityWidth - margin * 2);
      const y = insetY + margin + this.random.frac() * (cityHeight - margin * 2);
      const container = this.add.container(x, y);
      container.setDepth(32);

      const sprite = this.add.image(0, 0, 'guardian');
      sprite.setDisplaySize(70, 70);
      sprite.setTint(0xff6644);
      container.add([sprite]);

      const targetX = clamp(x + this.random.between(-300, 300), insetX + 60, insetX + cityWidth - 60);
      const targetY = clamp(y + this.random.between(-300, 300), insetY + 60, insetY + cityHeight - 60);

      this.state.patrolGuardians.push({
        x, y, targetX, targetY,
        speed: 55 + this.random.frac() * 40,
        display: container,
        waitTimer: 0,
      });
    }
  }

  updatePatrolGuardians(delta) {
    if (this.state.patrolGuardians.length === 0) return;
    const { insetX, insetY, cityWidth, cityHeight } = this.world;
    const monsterX = this.monster.container.x;
    const monsterY = this.monster.container.y;

    for (const pg of this.state.patrolGuardians) {
      if (pg.waitTimer > 0) {
        pg.waitTimer -= delta;
      } else {
        const dx = pg.targetX - pg.x;
        const dy = pg.targetY - pg.y;
        const dist = Math.hypot(dx, dy);
        if (dist < 8) {
          pg.targetX = clamp(insetX + 60 + this.random.frac() * (cityWidth - 120), insetX + 60, insetX + cityWidth - 60);
          pg.targetY = clamp(insetY + 60 + this.random.frac() * (cityHeight - 120), insetY + 60, insetY + cityHeight - 60);
          pg.waitTimer = 0.5 + this.random.frac() * 1.0;
        } else {
          pg.x += (dx / dist) * pg.speed * delta;
          pg.y += (dy / dist) * pg.speed * delta;
        }
      }

      pg.display.setPosition(pg.x, pg.y);
      const angle = Math.atan2(pg.targetY - pg.y, pg.targetX - pg.x);
      pg.display.setRotation(angle + Math.PI / 2);

      if (this.state.guardianHitCooldown > 0) continue;
      const distToMonster = Phaser.Math.Distance.Between(pg.x, pg.y, monsterX, monsterY);
      const hitRadius = 38 + this.getMonsterRadius() * 0.3;
      if (distToMonster < hitRadius) {
        this._applyGuardianHit(pg.x, pg.y, 160, '\u041f\u0430\u0442\u0440\u0443\u043b\u044c \u0442\u0435\u0431\u044f \u043f\u043e\u0439\u043c\u0430\u043b! -\u043c\u0430\u0441\u0441\u0430');
      }
    }
  }

  // ── Target Arrow ───────────────────────────────────────────────────────────


  updateTargetArrow() {
    this.targetArrow.clear();
    const swallowPower = this.getSwallowPower();
    if (swallowPower >= FINAL_TOWER.requiredSize) return;
    let nearest = null;
    let nearestDist = Infinity;
    for (const entity of this.entities) {
      if (!entity.alive || entity.isFinal) continue;
      if (entity.type.requiredSize > swallowPower) continue;
      const d = Phaser.Math.Distance.Between(entity.x, entity.y, this.monster.container.x, this.monster.container.y);
      if (d < nearestDist) { nearestDist = d; nearest = entity; }
    }
    if (!nearest || nearestDist < 80) return;
    const angle = Math.atan2(nearest.y - this.monster.container.y, nearest.x - this.monster.container.x);
    const cx = this.cameras.main.width / 2;
    const cy = this.cameras.main.height / 2;
    const arrowLen = 30;
    const arrowOffset = 80;
    const tx = cx + Math.cos(angle) * arrowOffset;
    const ty = cy + Math.sin(angle) * arrowOffset;
    this.targetArrow.fillStyle(0xffffff, 0.75);
    this.targetArrow.fillTriangle(
      tx + Math.cos(angle) * arrowLen, ty + Math.sin(angle) * arrowLen,
      tx + Math.cos(angle + 2.4) * 14,  ty + Math.sin(angle + 2.4) * 14,
      tx + Math.cos(angle - 2.4) * 14,  ty + Math.sin(angle - 2.4) * 14
    );
  }

  // ── Touch controls ─────────────────────────────────────────────────────────

  triggerAbilityFromTouch() {
    if (!this.state.ended && this.state.abilityCooldown <= 0) {
      this.state.abilityCooldown = this.monsterConfig.ability.cooldown;
      this.state.abilityTimer   = this.monsterConfig.ability.duration;
      this.options.audio.playAbility();
      this.showStatus(`${this.monsterConfig.ability.name} \u0430\u043a\u0442\u0438\u0432\u0438\u0440\u043e\u0432\u0430\u043d.`, 1700);
    }
  }

  triggerDashFromTouch() {
    if (!this.state.ended && this.state.dashCooldown <= 0) {
      this.state.dashCooldown = 3.5;
      this.state.dashTimer    = 0.28;
      this.options.audio.playDash();
      this.showStatus('\u0420\u044b\u0432\u043e\u043a!', 900);
    }
  }
}